import dayjs from 'dayjs';
import { CalendarDay } from '../types';

/**
 * Generates a complete calendar grid for the DatePicker component.
 *
 * @param currentMonth - Date object representing the base month to generate the calendar for
 * @returns Array of CalendarDay objects representing a complete calendar grid
 *
 * The grid structure:
 * - Sunday = index 0, Monday = index 1, ..., Saturday = index 6
 * - First element is always a Sunday, last element is always a Saturday
 * - Includes padding from previous/next months to fill the grid
 */
export function generateCalendarGrid(currentMonth: Date): CalendarDay[] {
  const today = dayjs();
  const baseMonth = dayjs(currentMonth);

  // Get the first and last day of the current month
  const firstDayOfMonth = baseMonth.startOf('month');
  const lastDayOfMonth = baseMonth.endOf('month');

  // Get the weekday of the first day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  // Keep Sunday-first format (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const firstDayWeekday = firstDayOfMonth.day();
  const sundayFirstWeekday = firstDayWeekday;

  // Get the weekday of the last day (keep Sunday-first format)
  const lastDayWeekday = lastDayOfMonth.day();
  const lastDaySundayFirst = lastDayWeekday;

  // Calculate the starting date (Sunday of the first week)
  const startDate = firstDayOfMonth.subtract(sundayFirstWeekday, 'day');

  // Calculate how many days from next month we need (to reach Saturday)
  const nextMonthDays = 6 - lastDaySundayFirst;

  // Calculate total days needed
  const totalDays = sundayFirstWeekday + baseMonth.daysInMonth() + nextMonthDays;

  const calendarDays: CalendarDay[] = [];

  for (let i = 0; i < totalDays; i += 1) {
    const currentDate = startDate.add(i, 'day');

    const calendarDay: CalendarDay = {
      date: currentDate,
      dayNumber: currentDate.date(),
      isCurrentMonth:
        currentDate.month() === baseMonth.month() && currentDate.year() === baseMonth.year(),
      isToday: currentDate.isSame(today, 'day'),
    };

    calendarDays.push(calendarDay);
  }

  return calendarDays;
}

/**
 * Helper function to get the week index for a given day in the calendar grid
 *
 * @param dayIndex - Index of the day in the calendar grid
 * @returns Week index (starting from 0)
 */
export function getWeekIndex(dayIndex: number): number {
  return Math.floor(dayIndex / 7);
}

/**
 * Helper function to get the day of week index (0-6) for a given day in the calendar grid
 *
 * @param dayIndex - Index of the day in the calendar grid
 * @returns Day of week index (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 */
export function getDayOfWeekIndex(dayIndex: number): number {
  return dayIndex % 7;
}

/**
 * Helper function to convert a week and day of week index to a calendar grid index
 *
 * @param weekIndex - Week index (starting from 0)
 * @param dayOfWeekIndex - Day of week index (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 * @returns Calendar grid index
 */
export function getCalendarIndex(weekIndex: number, dayOfWeekIndex: number): number {
  return weekIndex * 7 + dayOfWeekIndex;
}
